import { GetPermissionsResp } from '@/interfaces/role';

// 缓存键名
const PERMISSIONS_CACHE_KEY = 'agent_permissions_cache';
const CACHE_TIMESTAMP_KEY = 'agent_permissions_timestamp';

// 缓存有效期
const CACHE_EXPIRY_TIME = 72 * 60 * 60 * 1000;

/**
 * 从 localStorage 获取缓存的权限数据
 */
export const getCachedPermissions = (): GetPermissionsResp | null => {
  try {
    const cachedData = localStorage.getItem(PERMISSIONS_CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (!cachedData || !timestamp) {
      return null;
    }

    const cacheTime = parseInt(timestamp, 10);
    const now = Date.now();

    // 检查缓存是否过期
    if (now - cacheTime > CACHE_EXPIRY_TIME) {
      localStorage.removeItem(PERMISSIONS_CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      return null;
    }

    return JSON.parse(cachedData);
  } catch (error) {
    console.warn('Failed to parse cached permissions:', error);
    // 清除损坏的缓存
    localStorage.removeItem(PERMISSIONS_CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    return null;
  }
};

/**
 * 将权限数据保存到 localStorage
 */
export const setCachedPermissions = (data: GetPermissionsResp): void => {
  try {
    localStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Failed to cache permissions:', error);
  }
};

/**
 * 清除权限缓存
 */
export const clearPermissionsCache = (): void => {
  try {
    localStorage.removeItem(PERMISSIONS_CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  } catch (error) {
    console.warn('Failed to clear permissions cache:', error);
  }
};

/**
 * 检查两个权限数据是否一致
 */
export const isPermissionsEqual = (a: GetPermissionsResp, b: GetPermissionsResp): boolean => {
  if (!a || !b) return false;

  const keysA = Object.keys(a.mPerms || {});
  const keysB = Object.keys(b.mPerms || {});

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => {
    const permsA = a.mPerms[key] || [];
    const permsB = b.mPerms[key] || [];

    if (permsA.length !== permsB.length) return false;

    return permsA.every((perm) => permsB.includes(perm));
  });
};

/**
 * 获取缓存状态信息
 */
export const getCacheStatus = (): {
  hasCache: boolean;
  isExpired: boolean;
  cacheTime: number | null;
  timeUntilExpiry: number | null;
} => {
  try {
    const cachedData = localStorage.getItem(PERMISSIONS_CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (!cachedData || !timestamp) {
      return {
        hasCache: false,
        isExpired: false,
        cacheTime: null,
        timeUntilExpiry: null,
      };
    }

    const cacheTime = parseInt(timestamp, 10);
    const now = Date.now();
    const timeSinceCache = now - cacheTime;
    const isExpired = timeSinceCache > CACHE_EXPIRY_TIME;
    const timeUntilExpiry = isExpired ? 0 : CACHE_EXPIRY_TIME - timeSinceCache;

    return {
      hasCache: true,
      isExpired,
      cacheTime,
      timeUntilExpiry,
    };
  } catch (error) {
    console.warn('Failed to get cache status:', error);
    return {
      hasCache: false,
      isExpired: false,
      cacheTime: null,
      timeUntilExpiry: null,
    };
  }
};
